/**
 * Environment Variable Helper Utility
 *
 * Helps safely access environment variables from both client and server side
 * with proper fallbacks.
 */

/**
 * Access an environment variable safely from both client and server
 * @param {string} key - The base environment variable key without the NEXT_PUBLIC_ prefix
 * @returns {string|undefined} - The environment variable value or undefined if not found
 */
export function getEnv(key) {
  if (typeof window !== 'undefined') {
    // Client-side - can only access NEXT_PUBLIC_ prefixed variables
    return process.env[`NEXT_PUBLIC_${key}`];
  }

  // Server-side - try NEXT_PUBLIC_ prefix first, then fallback to direct key
  return process.env[`NEXT_PUBLIC_${key}`] || process.env[key];
}

/**
 * Get price ID for a subscription product safely
 * @param {string} productPrefix - Product prefix (VMC, GMC, CMC, MRM)
 * @param {number} period - Subscription period (1, 2, or 3 years)
 * @returns {string|undefined} - The price ID or undefined if not found
 */
export function getSubscriptionPriceId(productPrefix, period) {
  return getEnv(`STRIPE_${productPrefix}_${period}_YEAR_PRICE`);
}

/**
 * Get product ID for a subscription product safely
 * @param {string} productPrefix - Product prefix (VMC, GMC, CMC, MRM)
 * @param {number} period - Subscription period (1, 2, or 3 years)
 * @returns {string|undefined} - The product ID or undefined if not found
 */
export function getSubscriptionProductId(productPrefix, period) {
  return getEnv(`STRIPE_${productPrefix}_${period}_YEAR_PRODUCT`);
}

/**
 * Extract numeric period from period key
 * @param {string} periodKey - Period key like '1yr', '2yr', etc.
 * @returns {number} - Numeric period value
 */
function extractPeriodNumber(periodKey) {
  if (typeof periodKey === 'number') return periodKey;

  // Extract number from strings like '1yr', '2yr', etc.
  const match = periodKey.match(/^(\d+)/);
  if (match && match[1]) {
    return Number(match[1]);
  }

  // Default to 1 if no match found
  return 1;
}

/**
 * Helper to initialize subscription options object for a product
 * @param {string} productPrefix - Product prefix (VMC, GMC, CMC, MRM)
 * @param {Object} options - Price options for each period
 * @returns {Object} - Subscription options object with price and product IDs
 */
export function createSubscriptionOptions(productPrefix, options) {
  const result = {};

  // For each subscription period
  Object.keys(options).forEach((periodKey) => {
    // Convert period key to numeric value used in environment variables
    const periodNum = extractPeriodNumber(periodKey);

    // Original period key (like '1yr') is kept as the key in result
    result[periodKey] = {
      ...options[periodKey],
      priceId: getSubscriptionPriceId(productPrefix, periodNum),
      productId: getSubscriptionProductId(productPrefix, periodNum),
    };

    // Also add numeric key for direct access in the code that uses numeric period
    result[periodNum] = {
      ...options[periodKey],
      priceId: getSubscriptionPriceId(productPrefix, periodNum),
      productId: getSubscriptionProductId(productPrefix, periodNum),
    };
  });

  return result;
}

/**
 * Maps a product type string to a product prefix used in environment variables
 * @param {string} productType - Product type (e.g. 'verified-mark-certificate')
 * @returns {string} - Product prefix for environment variables (e.g. 'VMC')
 */
export function getProductPrefix(productType) {
  if (!productType) return null;

  const typeMap = {
    'verified-mark-certificate': 'VMC',
    'government-mark-certificate': 'GMC',
    'common-mark-certificate': 'CMC',
    'modified-registered-mark': 'CMC',
  };

  return typeMap[productType] || productType.toUpperCase();
}

/**
 * Gets a price ID for a product type and period from environment variables
 * @param {string} productType - The type of product (e.g., 'verified-mark-certificate')
 * @param {number} period - The period in years (1, 2, or 3)
 * @returns {string|null} - The price ID from environment variables, or null if not found
 */
export function getPriceIdFromEnv(productType, period) {
  if (!productType || !period) {
    return null;
  }

  // Validate and normalize inputs
  const normalizedProductType = productType.toLowerCase().trim();
  const normalizedPeriod = parseInt(period, 10);

  if (isNaN(normalizedPeriod) || normalizedPeriod < 1 || normalizedPeriod > 3) {
    return null;
  }

  // Special handling for test subscription product
  if (normalizedProductType === 'stripe-test-subscription') {
    const testKey = `TEST_CMC_${normalizedPeriod}_YEAR_PRICE`;
    return getEnv(`STRIPE_${testKey}`);
  }

  const prefix = getProductPrefix(normalizedProductType);
  if (!prefix) {
    return null;
  }

  // Construct the environment variable key
  // getEnv() will add NEXT_PUBLIC_ prefix automatically
  const key = `${prefix}_${normalizedPeriod}_YEAR_PRICE`;
  const priceId = getEnv(`STRIPE_${key}`);

  // Check fallback if primary key not found
  if (!priceId) {
    const fallbackKey = `${prefix}_PRICE`;
    return getEnv(`STRIPE_${fallbackKey}`);
  }

  return priceId;
}

export default {
  getEnv,
  getSubscriptionPriceId,
  getSubscriptionProductId,
  createSubscriptionOptions,
  getProductPrefix,
  getPriceIdFromEnv,
};
